<!-- biome-ignore-all lint/a11y/noStaticElementInteractions: the option role is bound dynamically to radio/checkbox -->
<script setup lang="ts">
  import { computed, inject, onBeforeUnmount, ref, useId, useSlots } from 'vue';
  import CheckboxIndicator from '../form/Checkbox/CheckboxIndicator/CheckboxIndicator.vue';
  import RadioIndicator from '../form/RadioGroup/RadioIndicator/RadioIndicator.vue';
  import { SELECTION_CARD_GROUP_CONTEXT_KEY } from '../SelectionCardGroup/SelectionCardGroup.context';
  import {
    backgroundClasses,
    badgeClass,
    bodyClass,
    boxClass,
    calloutClass,
    containerClass,
    ctaClass,
    ctaLabelClass,
    detailsClass,
    leadingClass,
    optionClass,
    ribbonClass,
    rowClass,
    sectionClass,
    titleClass,
    titleGroupClass,
  } from './SelectionCard.css';
  import {
    SelectionCardBackground,
    type SelectionCardEmits,
    SelectionCardMode,
    type SelectionCardProps,
    type SelectionCardSlots,
  } from './SelectionCard.types';

  const props = withDefaults(defineProps<SelectionCardProps>(), {
    selected: false,
    disabled: false,
    focused: false,
    mode: SelectionCardMode.SINGLE,
    background: SelectionCardBackground.WHITE,
  });

  const emit = defineEmits<SelectionCardEmits>();

  defineSlots<SelectionCardSlots>();

  const slots = useSlots();

  const group = inject(SELECTION_CARD_GROUP_CONTEXT_KEY, null);

  const optionRef = ref<HTMLElement | null>(null);

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-selection-card-${uid}`,
  );

  const isGrouped = computed(() => group !== null && props.value !== undefined);

  const isMultiple = computed(() =>
    group
      ? group.mode.value === SelectionCardMode.MULTIPLE
      : props.mode === SelectionCardMode.MULTIPLE,
  );

  const isDisabled = computed(
    () => props.disabled || (group?.disabled.value ?? false),
  );

  const isSelected = computed(() =>
    group && props.value !== undefined
      ? group.isSelected(props.value)
      : props.selected,
  );

  const cardRole = computed<'radio' | 'checkbox'>(() =>
    isMultiple.value ? 'checkbox' : 'radio',
  );

  const resolvedTabindex = computed(() => {
    if (isDisabled.value) return -1;
    if (group && isGrouped.value) {
      if (isMultiple.value) return 0;
      return group.tabStopId.value === uid ? 0 : -1;
    }
    return props.tabindex ?? 0;
  });

  const rootClasses = computed(() => [
    containerClass,
    backgroundClasses[props.background],
  ]);

  const hasRibbon = computed(() => !!slots.ribbon);
  const hasBadge = computed(() => !!slots.badge);
  const hasLeading = computed(() => !!slots.leading);
  const hasDetails = computed(() => !!props.details || !!slots.details);
  const hasCallout = computed(() => !!slots.callout);
  const hasBody = computed(() => !!slots.body);
  const hasCta = computed(() => !!props.ctaLabel || !!slots.cta);

  group?.register(uid, {
    value: () => props.value,
    disabled: () => isDisabled.value,
    element: () => optionRef.value,
  });

  onBeforeUnmount(() => group?.unregister(uid));

  function handleSelect(): void {
    if (isDisabled.value) return;
    if (!isMultiple.value && isSelected.value) return;
    if (group && props.value !== undefined) {
      group.select(props.value);
    }
    emit('update:selected', !isSelected.value);
    if (props.value !== undefined) {
      emit('select', props.value);
    }
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (isDisabled.value) return;
    if (event.key === ' ') {
      event.preventDefault();
      handleSelect();
    }
  }

  function focus(): void {
    optionRef.value?.focus();
  }

  defineExpose({ focus });
</script>

<template>
  <div
    :class="rootClasses"
    :data-selected="isSelected || undefined"
    :data-disabled="isDisabled || undefined"
    :data-focused="focused || undefined"
    :data-testid="`${resolvedTestId}-root`"
  >
    <div
      ref="optionRef"
      :class="optionClass"
      :role="cardRole"
      :aria-checked="isSelected"
      :aria-disabled="isDisabled || undefined"
      :aria-labelledby="`${resolvedTestId}-title`"
      :aria-describedby="hasDetails ? `${resolvedTestId}-details` : undefined"
      :tabindex="resolvedTabindex"
      :data-testid="resolvedTestId"
      @click="handleSelect"
      @keydown="handleKeydown"
    >
      <div
        v-if="hasRibbon"
        :class="ribbonClass"
        :data-testid="`${resolvedTestId}-ribbon`"
      >
        <slot name="ribbon" />
      </div>

      <div :class="sectionClass">
        <div :class="rowClass">
          <div
            v-if="hasLeading"
            :class="leadingClass"
            :data-testid="`${resolvedTestId}-leading`"
          >
            <slot name="leading" />
          </div>

          <div :class="boxClass">
            <div
              v-if="hasBadge"
              :class="badgeClass"
              :data-testid="`${resolvedTestId}-badge`"
            >
              <slot name="badge" />
            </div>

            <div :class="titleGroupClass">
              <span
                :id="`${resolvedTestId}-title`"
                :class="titleClass"
                :data-testid="`${resolvedTestId}-title`"
              >
                <slot name="title">{{ title }}</slot>
              </span>
              <p
                v-if="hasDetails"
                :id="`${resolvedTestId}-details`"
                :class="detailsClass"
                :data-testid="`${resolvedTestId}-details`"
              >
                <slot name="details">{{ details }}</slot>
              </p>
            </div>
          </div>

          <CheckboxIndicator
            v-if="isMultiple"
            :checked="isSelected"
            :disabled="isDisabled"
            :data-testid="`${resolvedTestId}-control`"
          />
          <RadioIndicator
            v-else
            :checked="isSelected"
            :disabled="isDisabled"
            :data-testid="`${resolvedTestId}-control`"
          />
        </div>

        <div
          v-if="hasCallout"
          :class="calloutClass"
          :data-testid="`${resolvedTestId}-callout`"
        >
          <slot name="callout" />
        </div>

        <div
          v-if="hasBody"
          :class="bodyClass"
          :data-testid="`${resolvedTestId}-body`"
        >
          <slot name="body" />
        </div>
      </div>
    </div>

    <div v-if="hasCta" :class="ctaClass">
      <slot name="cta">
        <span :class="ctaLabelClass">{{ ctaLabel }}</span>
      </slot>
    </div>
  </div>
</template>
